import { Bell, Save } from "lucide-react";
import { useState } from "react";

import { summarizeUsage } from "@/lib/format";
import { enablePushNotifications } from "@/lib/notifications";
import { saveSettings } from "@/lib/newsroom";
import type {
  NewsroomSettings,
  ProviderUsage,
} from "@/lib/types";

export function AppSettings({
  settings,
  usage,
  onSaved,
}: {
  settings: NewsroomSettings;
  usage: ProviderUsage[];
  onSaved: () => Promise<void>;
}) {
  const [draft, setDraft] = useState(settings);
  const [message, setMessage] = useState("");
  const usageTotals = summarizeUsage(usage);

  async function save() {
    setMessage("");
    try {
      await saveSettings(draft, settings.minimum_confidence);
      await onSaved();
      setMessage("Settings saved.");
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Unable to save settings",
      );
    }
  }

  async function enableNotifications() {
    setMessage("");
    try {
      await enablePushNotifications(settings.owner_id);
      setMessage("Browser notifications are enabled on this device.");
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "Unable to enable notifications",
      );
    }
  }

  return (
    <section className="settings-section">
      <header className="settings-section-header">
        <div>
          <p className="eyebrow">CONTROL ROOM</p>
          <h2>Drafting and verification</h2>
          <p>
            Verification is always rule-based. The selected model only rewrites
            drafts from the evidence already attached to a story.
          </p>
        </div>
      </header>

      <div className="settings-columns">
        <section className="settings-card">
          <div className="section-heading">
            <div>
              <p className="eyebrow">DRAFT PROVIDER</p>
              <h2>Default rewrite path</h2>
            </div>
          </div>
          <label>
            <span>Provider</span>
            <select
              value={draft.preferred_provider}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  preferred_provider: event.target
                    .value as NewsroomSettings["preferred_provider"],
                })
              }
            >
              <option value="deterministic">Deterministic templates</option>
              <option value="openai">OpenAI</option>
              <option value="openrouter">OpenRouter</option>
            </select>
          </label>
          <label>
            <span>OpenAI model</span>
            <input
              value={draft.openai_model}
              onChange={(event) =>
                setDraft({ ...draft, openai_model: event.target.value })
              }
            />
          </label>
          <label>
            <span>OpenRouter model</span>
            <input
              value={draft.openrouter_model}
              onChange={(event) =>
                setDraft({ ...draft, openrouter_model: event.target.value })
              }
            />
          </label>
          <p className="settings-hint">
            API keys are configured only in Supabase secrets and are never sent
            to this browser.
          </p>
        </section>

        <section className="settings-card">
          <div className="section-heading">
            <div>
              <p className="eyebrow">VERIFICATION</p>
              <h2>Readiness threshold</h2>
            </div>
            <strong>{Math.round(draft.minimum_confidence * 100)}%</strong>
          </div>
          <input
            className="range"
            type="range"
            min="0.5"
            max="0.95"
            step="0.01"
            value={draft.minimum_confidence}
            onChange={(event) =>
              setDraft({
                ...draft,
                minimum_confidence: Number(event.target.value),
              })
            }
          />
          <p className="settings-hint">
            Ordinary claims still need two independent domains. A trusted
            primary source can qualify on its own.
          </p>
          <label>
            <span>Retention days</span>
            <input
              type="number"
              min={7}
              max={365}
              value={draft.retention_days}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  retention_days: Number(event.target.value),
                })
              }
            />
          </label>
        </section>

        <section className="settings-card budget-card">
          <div className="section-heading">
            <div>
              <p className="eyebrow">DAILY LIMITS</p>
              <h2>Provider budgets</h2>
            </div>
          </div>
          <BudgetInput
            label="Exa searches"
            value={draft.exa_daily_limit}
            used={usageTotals.get("exa") ?? 0}
            onChange={(value) => setDraft({ ...draft, exa_daily_limit: value })}
          />
          <BudgetInput
            label="Firecrawl pages"
            value={draft.firecrawl_daily_limit}
            used={usageTotals.get("firecrawl") ?? 0}
            onChange={(value) =>
              setDraft({ ...draft, firecrawl_daily_limit: value })
            }
          />
          <BudgetInput
            label="Model rewrites"
            value={draft.model_daily_limit}
            used={
              (usageTotals.get("openai") ?? 0) +
              (usageTotals.get("openrouter") ?? 0)
            }
            onChange={(value) =>
              setDraft({ ...draft, model_daily_limit: value })
            }
          />
        </section>

        <section className="settings-card notification-card">
          <Bell aria-hidden="true" />
          <div>
            <h2>Story-ready notifications</h2>
            <p>
              Telegram alerts are configured in Supabase secrets and include a
              link plus Short and Long buttons for manually generated drafts.
              Web Push can also be enabled per device.
            </p>
          </div>
          <button
            type="button"
            className="button-secondary"
            onClick={() => void enableNotifications()}
          >
            Enable notifications
          </button>
        </section>
      </div>

      <div className="settings-save">
        {message ? <p className="inline-message">{message}</p> : null}
        <button type="button" className="button-primary" onClick={() => void save()}>
          <Save aria-hidden="true" />
          Save settings
        </button>
      </div>
    </section>
  );
}

function BudgetInput({
  label,
  value,
  used,
  onChange,
}: {
  label: string;
  value: number;
  used: number;
  onChange: (value: number) => void;
}) {
  const percentage = value > 0 ? Math.min(100, (used / value) * 100) : 100;
  return (
    <label className="budget-input">
      <span>
        <b>{label}</b>
        <small>
          {used} / {value} used
        </small>
      </span>
      <span className="budget-track">
        <i style={{ width: `${percentage}%` }} />
      </span>
      <input
        type="number"
        min={0}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  );
}
